import PropTypes from 'prop-types';
import styles from './FJVideoInput.module.less';
import FJLocalStore from '../../../store/FJLocalStore';
import classNames from 'classnames';
import FJToolTip from '../FJToolTip/FJToolTip';
import { useRef } from 'react';
import DeleteIcon from '../../../assets/svg/delete-x-white.svg?react';
import UploadCircleIcon from '../../../assets/svg/upload-circle.svg?react';
import ReplaceIcon from '../../../assets/svg/replace.svg?react';
import DragReplaceIcon from '../../../assets/svg/drag-replace.svg?react';
import FJUtil from '../../../util/FJUtil';
import { useMemo } from 'react';
import DeleteBlackIcon from '../../../assets/svg/delete-1.svg?react';
import CropIcon from '../../../assets/svg/crop.svg?react';
import FJVideoPreviewBox from '../FJVideoPreviewBox/FJVideoPreviewBox';
import FJLoading from '../FJLoading/FJLoading';

const FJVideoInput = props => {
    const {
        uploadVideoInfo = {},
        isDraggingFile,

        style,

        acceptFile,
        isLoading = false,
        disable = false,
        isDelete = false,
        isMultiple = false,
        isShowCrop = false,

        leftChild,
        uploadText = FJLocalStore._('upload-image'),

        uploadVideo,
        onDelete,
        onCrop,
        openMediaLibrary,
        setIsDraggingFile,
    } = props;
    const { src: videoSrc, name } = uploadVideoInfo;

    const inputRef = useRef(null);
    const _onUploadImg = e => {
        if (disable) return;
        if (isMultiple) {
            const files = Array.from(e.target.files);
            uploadVideo(files);
        } else {
            const file = e.target.files[0];
            uploadVideo(file);
        }
        e.target.value = null;
    };
    const _onUpload = e => {
        e.stopPropagation();
        if (disable) {
            return;
        }

        if (window.isEditPage && openMediaLibrary) {
            openMediaLibrary();
        } else if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const _onDelete = e => {
        e.stopPropagation();
        if (disable) {
            return;
        }
        onDelete && onDelete();
    };

    const _onCrop = e => {
        e.stopPropagation();
        if (disable) {
            return;
        }
        onCrop && onCrop();
    };

    const _onDragOver = e => {
        if (disable) return;
        e.stopPropagation();
        e.preventDefault();
        setIsDraggingFile(true);
    };

    const _onDragLeave = e => {
        if (disable) return;
        e.stopPropagation();
        e.preventDefault();
        setIsDraggingFile(false);
    };

    const _onDragDrop = e => {
        if (disable) return;
        e.stopPropagation();
        e.preventDefault();
        setIsDraggingFile(false);

        let files = Array.from(e.dataTransfer.files);
        if (files && files.length > 0) {
            if (isMultiple) {
                uploadVideo(files);
            } else {
                const file = files[0];
                uploadVideo(file);
            }
        }
    };

    const containerWidth = 327;
    const fileName = useMemo(() => {
        let { fileText } = FJUtil.getEspText(name, 13, containerWidth - 5);
        return fileText;
    }, [name]);

    return (
        <div
            className={classNames(styles.input_box, {
                [styles.disabled]: disable,
            })}
            onDragOver={_onDragOver}
            onDrop={_onDragDrop}
            onDragLeave={_onDragLeave}
            style={{ height: '64px', ...style }}
        >
            {videoSrc ? (
                <div className={classNames(styles.img_content, { [styles.hide]: isLoading })} onClick={_onUpload}>
                    {!isLoading && (
                        <>
                            <div className={styles.video_box}>
                                <video className={styles.video} src={videoSrc} alt='' loading='lazy' />
                                <FJVideoPreviewBox videoInfo={uploadVideoInfo} className={styles.mask} />
                            </div>
                            <div className={styles.video_info}>
                                <span className={styles.video_info_text} title={fileName === name ? '' : name}>
                                    {!fileName || fileName === '' ? FJLocalStore._('image') : fileName}
                                </span>
                            </div>
                            {!isShowCrop && (
                                <FJToolTip title={isDelete ? FJLocalStore._('delete') : FJLocalStore._('change-image')}>
                                    <div
                                        className={classNames(
                                            styles.reload_icon,
                                            disable ? styles.reload_icon_disabled : '',
                                        )}
                                        onClick={isDelete ? _onDelete : _onUpload}
                                    >
                                        {isDelete ? <DeleteIcon /> : <ReplaceIcon />}
                                    </div>
                                </FJToolTip>
                            )}

                            {isShowCrop && (
                                <>
                                    <FJToolTip title={FJLocalStore._('delete')}>
                                        <div
                                            className={classNames(styles.delete_icon, {
                                                [styles.disabled]: disable,
                                            })}
                                            onClick={_onDelete}
                                        >
                                            <DeleteBlackIcon />
                                        </div>
                                    </FJToolTip>

                                    <FJToolTip title={FJLocalStore._('trim')}>
                                        <div
                                            className={classNames(styles.crop_icon, {
                                                [styles.disabled]: disable,
                                            })}
                                            onClick={_onCrop}
                                        >
                                            <CropIcon />
                                        </div>
                                    </FJToolTip>
                                </>
                            )}
                        </>
                    )}

                    {isLoading && (
                        <div className={styles.loading}>
                            <FJLoading strokeColor='#a8acbc' />
                        </div>
                    )}
                </div>
            ) : (
                <div className={classNames(styles.blank, isDraggingFile && styles.dragging)} onClick={_onUpload}>
                    <div className={styles.upload_border_box}></div>

                    {!isLoading && (
                        <>
                            <UploadCircleIcon />
                            <span className={styles.blank_text}>{uploadText}</span>
                            {leftChild}
                        </>
                    )}

                    {isLoading && (
                        <div className={styles.loading}>
                            <FJLoading strokeColor='#a8acbc' />
                        </div>
                    )}
                </div>
            )}

            {isDraggingFile && videoSrc && (
                <div className={classNames(styles.filter_mask, isDraggingFile ? styles.dragging : '')}>
                    <DragReplaceIcon />
                    <span className={styles.filter_mask_text}>{FJLocalStore._('drag-and-drop')}</span>
                </div>
            )}
            <div className={styles.dragging_mask}></div>

            <input
                type='file'
                ref={inputRef}
                className={styles.file_input}
                accept={acceptFile}
                multiple={isMultiple}
                onChange={_onUploadImg}
            />
        </div>
    );
};
FJVideoInput.propTypes = {
    uploadVideoInfo: PropTypes.object,
    uploadVideo: PropTypes.func,
    isDraggingFile: PropTypes.bool,
    setIsDraggingFile: PropTypes.func,
    openMediaLibrary: PropTypes.func,
    acceptFile: PropTypes.string,
    disable: PropTypes.bool,
};
export default FJVideoInput;
